
// Relative, not `/HexLife/…`: an absolute path works under the dev server's base but is not
// rewritten at build time, so it would 404 on the deployed site. This is the same source tree the
// npm package publishes — `@hexlife/embed` ships `src/`, so these two imports are exactly the
// package's two entry points.
import './src/embed/index.js';
import {
  listPresetPalettes, describeRuleset, rulesetName, rulesetToCode, explorerUrlForRuleset,
  encodeWorldCode,
} from './src/embed/api.js';

const grid = document.getElementById('grid');
const marker = document.getElementById('marker');
const stage = document.querySelector('.stage');

/*
 * Tiles on the map, as columns × rows.
 *
 * 16×16 is not arbitrary — 256 tiles is exactly the size of the totalistic class, so at that layout
 * the page shows a complete rule space rather than a sample of one. But it is a *default*, not a
 * constant: a phone cannot draw 256 legible tiles, and on a wide screen a 24×8 strip is a perfectly
 * good way to read a class. Everything downstream asks `tileCount()`, and the banner always says
 * which of the two situations you are in.
 */

/** Most tiles on one axis. Past this the tiles are smaller than the gap between them. */
const AXIS_MAX = 24;
/**
 * Worlds, not tiles. Each one is its own wasm grid of 96×112 cells ticking every frame, so the cap
 * is about what a laptop can actually animate — the element's own backstop is four times higher.
 */
const MAX_TILES = 400;
/** A tile below this many CSS px is a smudge rather than a world; used to pick the default only. */
const MIN_TILE_PX = 26;

const tileCount = () => layoutCols * layoutRows;
/** How many rulesets the class contains — `Infinity`-large for `free`, which is the whole point. */
const classSize = (cls) => 2 ** cls.bits;
/** True when the class fits on the map with nothing sampled away. */
const isExhaustive = () => classSize(currentClass()) <= tileCount();

const clampAxis = (v) => Math.min(AXIS_MAX, Math.max(1, Math.floor(Number(v)) || 1));

/**
 * Both axes in range, and never more worlds than {@link MAX_TILES}.
 * @param {'cols'|'rows'} shrink Which axis yields when the product is too big — the *other* one is
 *   the one the viewer just typed, and an input that silently rewrites what you typed is a bad
 *   control.
 */
function clampLayout(cols, rows, shrink = 'rows') {
  let c = clampAxis(cols);
  let r = clampAxis(rows);
  if (c * r > MAX_TILES) {
    if (shrink === 'cols') c = Math.max(1, Math.floor(MAX_TILES / r));
    else r = Math.max(1, Math.floor(MAX_TILES / c));
  }
  return {cols: c, rows: r};
}

/**
 * `g=COLSxROWS` off the query string, or the largest square this screen can draw legibly.
 *
 * The default is responsive rather than pinned at 16×16 because 256 tiles on a phone is 21 px of
 * world each: the honest answer there is fewer, larger tiles plus a banner that says it is showing
 * 144 of the 256. Every link carries `g`, so a view that was worth sharing is reproduced exactly,
 * whatever it is opened on.
 */
function readLayoutParam(raw) {
  const m = /^(\d+)\s*[x×*]\s*(\d+)$/.exec(String(raw || '').trim());
  if (m) return clampLayout(m[1], m[2]);
  // Deliberately rough: this only picks a starting point, and `fitStage` does the real sizing.
  const side = Math.min(
    document.documentElement.clientWidth - 40,
    document.documentElement.clientHeight - 280,
  );
  for (const n of [16, 12, 8, 6, 4]) {
    if (side / n >= MIN_TILE_PX) return {cols: n, rows: n};
  }
  return {cols: 4, rows: 4};
}

// --- the rule classes --------------------------------------------------------
/**
 * The nested constraint hierarchy, in the package's own short-code notation. Each class is a
 * perfect bijection with a fixed-width payload (see `core/rulesetCode.js`), so "a random member of
 * this class" is literally "the tag letter plus a random payload" — no rejection sampling, no
 * canonicalisation, and the count of members is exactly 2^bits.
 *
 * `free` has no tag: an unconstrained rule *is* its 32-char hex, all 128 entries independent.
 */
const CLASSES = [
  {
    key: 'totalistic', tag: 'T', bits: 8, chars: 2,
    label: 'Totalistic — all 256', chip: 'Totalistic',
    unit: 'total-count bucket',
    note: 'The output depends only on the <b>total</b> live count, centre included: 8 buckets, and every one of the 2<sup>8</sup> assignments is on screen.',
  },
  {
    key: 'n_count', tag: 'N', bits: 14, chars: 4,
    label: 'Outer-totalistic (neighbour count)', chip: 'Outer-totalistic',
    unit: 'neighbour-count bucket',
    note: 'The output depends on the centre state <em>and</em> how many of the six neighbours are alive, but not on <em>which</em>: 2 × 7 = 14 buckets. The totalistic class is the subset where bucket <code>(1, n)</code> always equals <code>(0, n+1)</code>.',
  },
  {
    key: 'd_sym', tag: 'M', bits: 26, chars: 7,
    label: 'Dihedral-symmetric (D6)', chip: 'Dihedral D6',
    unit: 'D6 orbit',
    note: 'The output is invariant under all 12 rotations <em>and</em> reflections of the hex — the 64 neighbourhoods collapse into 13 orbits, × 2 centre states = 26 free bits. Tagged <code>M</code> for mirror, because <code>D</code> is a hex digit.',
  },
  {
    key: 'r_sym', tag: 'R', bits: 28, chars: 7,
    label: 'Rotationally symmetric (C6)', chip: 'Rotational C6',
    unit: 'C6 orbit',
    note: 'The output is invariant under the 6 rotations of the hex but not under reflection — 14 orbits × 2 centre states = 28 free bits. This is the widest class that still looks the same whichever way you turn the paper.',
  },
  {
    key: 'free', tag: null, bits: 128, chars: 32,
    label: 'Unconstrained — the whole space', chip: 'Unconstrained',
    unit: 'rule entry',
    note: 'No constraint at all: each of the 128 neighbourhood entries is drawn on its own. Almost everything here is chaos — that is the honest shape of rule space, and why the constrained classes above are where the interesting rules live.',
  },
];
const CLASS_BY_KEY = new Map(CLASSES.map((c) => [c.key, c]));

// --- random draws ------------------------------------------------------------
/** mulberry32 — a seeded PRNG, so a sample is a URL rather than a lucky accident. */
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * One random member of a class, as a ruleset code.
 *
 * Every payload bit is an independent Bernoulli(bias) draw — the same shape as the Explorer's own
 * "generate random ruleset", where `bias` is the probability that a bucket/orbit/entry fires a live
 * cell. At 0.5 this is a uniform draw from the class; away from 0.5 it is deliberately not.
 */
function drawCode(cls, rng, bias) {
  if (!cls.tag) {
    // 128 bits do not fit a JS number, so the payload is built a nibble at a time — which for the
    // free class *is* the 32-char hex, no tag.
    let hex = '';
    for (let i = 0; i < 32; i++) {
      let nibble = 0;
      for (let b = 0; b < 4; b++) nibble = nibble * 2 + (rng() < bias ? 1 : 0);
      hex += nibble.toString(16).toUpperCase();
    }
    return hex;
  }
  let value = 0;
  for (let i = 0; i < cls.bits; i++) value = value * 2 + (rng() < bias ? 1 : 0);
  return cls.tag + value.toString(16).toUpperCase().padStart(cls.chars, '0');
}

/**
 * The `size` codes to put on the map.
 *
 * A class no bigger than the grid is *enumerated*, not sampled — 256 totalistic rules on 256 tiles,
 * and showing anything other than all of them in code order would be a worse map. Bias is ignored
 * there for the same reason: there is nothing to choose. Shrink the grid below the class and it
 * stops being a complete map and becomes a sample of one, which is exactly what the wider classes
 * always were; the code path they share says so.
 *
 * Otherwise: draw without replacement. Duplicates are not merely untidy, they are a lie about how
 * much of the space you are looking at. An extreme bias can shrink the reachable set below `size`
 * (bias 0.05 draws the all-dead rule very often), so the guard gives up rather than spinning, and
 * the banner reports how many distinct rules actually made it.
 *
 * @returns {{codes: string[], distinct: number}}
 */
function drawSample(cls, seed, bias, size) {
  if (classSize(cls) <= size) {
    const all = Array.from(
      {length: classSize(cls)},
      (_, i) => cls.tag + i.toString(16).toUpperCase().padStart(cls.chars, '0'),
    );
    const codes = all.slice(0, size);
    for (let i = 0; codes.length < size; i++) codes.push(all[i % all.length]);
    return {codes, distinct: all.length};
  }
  const rng = mulberry32(seed);
  const seen = new Set();
  const codes = [];
  for (let guard = 0; codes.length < size && guard < size * 60; guard++) {
    const code = drawCode(cls, rng, bias);
    if (seen.has(code)) continue;
    seen.add(code);
    codes.push(code);
  }
  const distinct = codes.length;
  // Pad rather than shrink the grid: a short list would leave the last tiles blank, and every tile
  // showing a rule is worth more than the tail being unique.
  for (let i = 0; codes.length < size; i++) codes.push(codes[i % distinct]);
  return {codes, distinct};
}

// --- number formatting -------------------------------------------------------
/** Grouped digits while they stay readable, scientific once they do not. */
function fmtBig(n) {
  const s = n.toString();
  if (s.length <= 12) return Number(n).toLocaleString();
  return `${s[0]}.${s.slice(1, 3)} × 10<sup>${s.length - 1}</sup>`;
}

// --- state -------------------------------------------------------------------
const url = new URL(window.location.href);
const q = url.searchParams;
const randomSeed = () => (Math.random() * 0x100000000) >>> 0;

let classKey = CLASS_BY_KEY.has(q.get('c')) ? q.get('c') : 'totalistic';
let sampleSeed = /^[0-9a-z]+$/.test(q.get('s') || '') ? parseInt(q.get('s'), 36) >>> 0 : randomSeed();
let bias = num(q.get('b'), 0.05, 0.95, 0.5);
let icMode = ['single', 'random', 'clusters'].includes(q.get('ic')) ? q.get('ic') : 'random';
let clusterSeed = /^[0-9a-z]+$/.test(q.get('cs') || '') ? parseInt(q.get('cs'), 36) >>> 0 : 0x9e3779b9;
let {cols: layoutCols, rows: layoutRows} = readLayoutParam(q.get('g'));
let currentCodes = [];
let distinctCount = 0;
/** The tile the marker is parked on, or -1. Kept because a resize moves every tile. */
let selectedIndex = -1;

/**
 * Read one numeric knob out of the query string. An absent or unparseable value takes `fallback`
 * rather than clamping — `Number(null)` is 0, which would silently pin every missing slider to its
 * minimum instead of its default.
 * @param {string|null|undefined} raw
 */
function num(raw, lo, hi, fallback) {
  if (raw === null || raw === undefined || raw === '') return fallback;
  const v = Number(raw);
  return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback;
}

const currentClass = () => CLASS_BY_KEY.get(classKey);

// --- cluster tool ------------------------------------------------------------
/**
 * A mini port of the Explorer's initial-state cluster tool: identical param keys, identical ranges,
 * and {@link paintClusters} is a line-for-line port of `core/initialStateStrategies/ClusterStrategy`.
 * Params therefore transfer both ways — a bundle tuned here means the same thing in the Explorer.
 *
 * Only `eccentricity` is narrowed (0.98 rather than 1.0): at exactly 1 the minor axis is zero and
 * every cell divides by it, so the blob vanishes. That is a range choice, not a maths change.
 */
const CLUSTER_CONTROLS = [
  {head: 'Basics'},
  {key: 'count', label: 'Clumps', min: 1, max: 50, step: 1, help: 'How many separate blobs of live cells to scatter across the world.'},
  {key: 'diameter', label: 'Clump size', min: 5, max: 100, step: 1, help: 'Roughly how wide each blob is, in cells.'},
  {key: 'density', label: 'Fill amount', min: 0, max: 1, step: 0.01, help: 'How solidly each blob is filled (0 = empty, 1 = packed).'},
  {head: 'Shape'},
  {key: 'gaussianStdDev', label: 'Center focus', min: 0.5, max: 5, step: 0.1, help: 'Higher values pack cells toward each blob’s center, leaving soft, thin edges.'},
  {key: 'eccentricity', label: 'Stretch', min: 0, max: 0.98, step: 0.01, help: '0 = round blobs, 1 = long thin streaks.'},
  {key: 'orientation', label: 'Angle', min: 0, max: 180, step: 1, help: 'Direction stretched blobs point, in degrees.'},
  {head: 'Variation'},
  {key: 'diameterVariation', label: 'Size ±', min: 0, max: 50, step: 1, help: 'How much blob sizes differ from one another.'},
  {key: 'densityVariation', label: 'Fill ±', min: 0, max: 1, step: 0.01, help: 'How much the fill amount varies between blobs.'},
  {key: 'orientationVariation', label: 'Angle ±', min: 0, max: 1, step: 0.01, help: 'How much blob angles differ from one another.'},
];
const CLUSTER_KEYS = CLUSTER_CONTROLS.filter((c) => c.key).map((c) => c.key);

/** The Explorer's frozen cluster presets, verbatim (`core/initialStatePresets.js`). */
const CLUSTER_PRESETS = [
  {name: 'Scattered', params: {count: 35, density: 0.6, densityVariation: 0.2, diameter: 6, diameterVariation: 3, eccentricity: 0.2, orientation: 0, orientationVariation: 1.0, gaussianStdDev: 2.5}},
  {name: 'Islands', params: {count: 12, density: 0.75, densityVariation: 0.15, diameter: 18, diameterVariation: 6, eccentricity: 0.3, orientation: 0, orientationVariation: 1.0, gaussianStdDev: 2.0}},
  {name: 'Big blobs', params: {count: 5, density: 0.8, densityVariation: 0.1, diameter: 42, diameterVariation: 10, eccentricity: 0.2, orientation: 0, orientationVariation: 1.0, gaussianStdDev: 1.6}},
  {name: 'Streaks', params: {count: 14, density: 0.7, densityVariation: 0.2, diameter: 24, diameterVariation: 8, eccentricity: 0.82, orientation: 30, orientationVariation: 0.6, gaussianStdDev: 2.6}},
];
const CLUSTER_DEFAULTS = {count: 25, density: 0.7, densityVariation: 0.2, diameter: 10, diameterVariation: 5, eccentricity: 0.33, orientation: 0, orientationVariation: 1.0, gaussianStdDev: 2.0};

/** Cluster params come off the URL as one comma-joined list, in `CLUSTER_KEYS` order. */
const clusterParams = {...CLUSTER_DEFAULTS};
{
  const raw = String(q.get('cl') || '').split(',');
  if (raw.length === CLUSTER_KEYS.length) {
    CLUSTER_KEYS.forEach((key, i) => {
      const def = CLUSTER_CONTROLS.find((c) => c.key === key);
      clusterParams[key] = num(raw[i], def.min, def.max, CLUSTER_DEFAULTS[key]);
    });
  }
}

/**
 * Port of `ClusterStrategy.generate` — elliptical, gaussian-falloff blobs on a torus, placed with a
 * 50-attempt non-overlap search. Kept faithful (including its degenerate corners) so that a param
 * bundle means exactly the same picture here and in the Explorer.
 */
function paintClusters(cells, cols, rows, p, rng) {
  const placed = [];
  const overlaps = (x, y, d) => placed.some((o) => {
    const dx = Math.abs(x - o.x), dy = Math.abs(y - o.y);
    const tx = Math.min(dx, cols - dx), ty = Math.min(dy, rows - dy);
    const need = d / 2 + o.d / 2;
    return tx * tx + ty * ty < need * need;
  });

  for (let i = 0; i < p.count; i++) {
    let x = 0, y = 0, d = 0, attempts = 0;
    do {
      x = rng() * cols;
      y = rng() * rows;
      d = p.diameter + (rng() * 2 - 1) * p.diameterVariation;
      attempts++;
      if (attempts > 50) break;
    } while (overlaps(x, y, d));
    placed.push({x, y, d});

    const angle = (p.orientation + (rng() * 2 - 1) * p.orientationVariation * 180) * Math.PI / 180;
    const cosA = Math.cos(angle), sinA = Math.sin(angle);
    const major = d / 2;
    const minor = major * (1 - p.eccentricity);
    const stdDev = d / (p.gaussianStdDev * 2);
    // Twice the radius: the gaussian tail reaches past the nominal edge.
    const searchRadius = Math.ceil(major * 2);

    for (let ro = -searchRadius; ro <= searchRadius; ro++) {
      for (let co = -searchRadius; co <= searchRadius; co++) {
        const c = Math.round(x + co), r = Math.round(y + ro);
        const dx = (c - x + cols / 2 + cols) % cols - cols / 2;
        const dy = (r - y + rows / 2 + rows) % rows - rows / 2;
        const rotX = dx * cosA + dy * sinA;
        const rotY = dy * cosA - dx * sinA;
        const normalized = Math.sqrt((rotX / major) ** 2 + (rotY / minor) ** 2);
        let prob = Math.max(0, Math.min(1, p.density + (rng() * 2 - 1) * p.densityVariation));
        prob *= Math.exp(-0.5 * ((normalized * major) / stdDev) ** 2);
        if (prob > 0 && rng() < prob) {
          cells[(((r % rows) + rows) % rows) * cols + (((c % cols) + cols) % cols)] = 1;
        }
      }
    }
  }
}

// --- initial conditions ------------------------------------------------------
/**
 * The exact cells currently seeded into every world, or null while the seed + density generator is
 * in charge. Kept because the detail view has to reproduce *this* initial condition — see openDetail.
 */
let icTemplate = null;

/**
 * Build the shared tick-0 grid for the current IC mode, or null to hand the worlds back to the
 * element's own seed + density fill. Painted ICs are computed once and replayed into every sim —
 * the tiles must differ by rule alone, so a per-world re-roll would break the whole premise.
 */
function buildTemplate() {
  if (icMode === 'random') return null;
  const world = grid.worldAt(0);
  if (!world) return null;
  const {cols, rows} = world;
  const cells = new Uint8Array(cols * rows);
  if (icMode === 'single') {
    cells[Math.floor(rows / 2) * cols + Math.floor(cols / 2)] = 1;
  } else {
    paintClusters(cells, cols, rows, clusterParams, mulberry32(clusterSeed));
  }
  return cells;
}

/** Re-seed every world with the current IC, from generation 0. */
function applyIC() {
  if (!grid.count) return;
  icTemplate = buildTemplate();
  grid.setInitialCells(icTemplate);
  syncStatus();
}

/** Slider drags land per pixel; one re-seed per frame is plenty. */
let icPending = 0;
function applyICSoon() {
  if (icPending) return;
  icPending = requestAnimationFrame(() => { icPending = 0; applyIC(); });
}

// --- controls ----------------------------------------------------------------
const playBtn = document.getElementById('playBtn');
const speedInput = document.getElementById('speed');
const speedOut = document.getElementById('speedOut');
const densityInput = document.getElementById('density');
const densityOut = document.getElementById('densityOut');
const densityGroup = document.getElementById('densityGroup');
const paletteSelect = document.getElementById('palette');
const hueShiftInput = document.getElementById('hueShift');
const hueShiftOut = document.getElementById('hueShiftOut');
const classSelect = document.getElementById('classSelect');
const biasInput = document.getElementById('bias');
const biasOut = document.getElementById('biasOut');
const biasGroup = document.getElementById('biasGroup');
const rerollGroup = document.getElementById('rerollGroup');
const rerollBtn = document.getElementById('rerollBtn');
const seedOut = document.getElementById('seedOut');
const sampleOut = document.getElementById('sample');
const clusterPanel = document.getElementById('clusterPanel');
const gridColsInput = document.getElementById('gridCols');
const gridRowsInput = document.getElementById('gridRows');
const tileCountOut = document.getElementById('tileCount');

const resetBtn = document.getElementById('resetBtn');
const stepBtn = document.getElementById('stepBtn');

playBtn.addEventListener('click', () => (grid.userPaused ? grid.play() : grid.pause()));
resetBtn.addEventListener('click', applyIC);
stepBtn.addEventListener('click', () => { grid.tick(1); syncStatus(); });

// --- map size ----------------------------------------------------------------
/**
 * Fit the map to the room that is actually left below the controls.
 *
 * A square that is `width: 100%` of a 1180 px column is 1180 px tall, and almost no screen has that
 * much viewport under a header and two control bars — which is why the page used to demand scrolling
 * to see a grid whose entire premise is that you see it at once. The height is the binding
 * constraint on nearly every display, so it is the one we solve for: take the space between the top
 * of the stage and the bottom of the viewport, and give the box the width that makes it exactly that
 * tall at the current tile aspect ratio.
 *
 * `clientHeight` rather than `visualViewport`: the layout viewport does not twitch as mobile browser
 * chrome slides in and out, and a map that resizes while you scroll is worse than one that is 60 px
 * short of perfect.
 */
const STAGE_RESERVE = 56;
const FULLSCREEN_STAGE_RESERVE = 42;
/** Past this the map is too small to read; scrolling is the better trade. */
const STAGE_MIN_H = 260;

/**
 * Synchronous on purpose. It is one `getBoundingClientRect` and one custom-property write, its
 * callers (`ResizeObserver`, the `resize` event) are already coalesced by the browser, and the box
 * it writes to is below everything it measures — so there is no loop to break. Deferring it to
 * `requestAnimationFrame` would only mean the map stays unsized in a tab that loads in the
 * background, where rAF never runs.
 */
function fitStage() {
  const top = stage.getBoundingClientRect().top;
  const reserve = document.body.classList.contains('atlas-fullscreen')
    ? FULLSCREEN_STAGE_RESERVE
    : STAGE_RESERVE;
  const avail = Math.max(STAGE_MIN_H, document.documentElement.clientHeight - top - reserve);
  // On the root, not on the stage: the workspace needs the same number to decide how wide the
  // two-column layout may grow, and a custom property only travels downwards.
  const root = document.documentElement.style;
  root.setProperty('--stage-max-w', `${Math.round(avail * (layoutCols / layoutRows))}px`);
  root.setProperty('--panel-max-h', `${Math.round(avail)}px`);
  positionMarker();
}

/** Park the selection outline on its tile, wherever that tile is now. */
function positionMarker() {
  const rect = selectedIndex >= 0 ? grid.tileRect(selectedIndex) : null;
  if (!rect) { marker.hidden = true; return; }
  Object.assign(marker.style, {
    left: `${rect.x}px`, top: `${rect.y}px`,
    width: `${rect.width}px`, height: `${rect.height}px`,
  });
  marker.hidden = false;
}

// The header is the only thing left above the map — everything else is beside it or below it,
// which is the entire point of the rail. Folding the credit away is therefore the one page change
// that moves the map, and this is what notices. It cannot observe the stage it sizes, so there is
// no observer feeding itself.
new ResizeObserver(fitStage).observe(document.querySelector('header'));
window.addEventListener('resize', fitStage);

/**
 * Hide the whole rail.
 *
 * On a landscape screen this does not make the map bigger — it is bound by height, and the rail
 * costs it nothing — so this is for looking at the thing without the instrument around it. The four
 * hotkeys keep working while it is hidden, which is why they are on the button's tooltip.
 */
const panelToggle = document.getElementById('panelToggle');
panelToggle.addEventListener('click', () => {
  const hidden = document.body.classList.toggle('no-panel');
  panelToggle.setAttribute('aria-pressed', String(!hidden));
  fitStage();
});

// --- workspace fullscreen ---------------------------------------------------
const fullscreenToggle = document.getElementById('fullscreenToggle');
const fullscreenExit = document.getElementById('fullscreenExit');
let ownsNativeFullscreen = false;

/** Apply the workspace-only layout independently of the browser's fullscreen capability. */
function applyFullscreenLayout(active) {
  document.body.classList.toggle('atlas-fullscreen', active);
  fullscreenToggle.setAttribute('aria-pressed', String(active));
  fullscreenToggle.textContent = active ? 'Exit fullscreen' : 'Fullscreen';
  fitStage();
  writeUrl();
}

/**
 * Enter native fullscreen when possible, but keep the workspace focus layout if it is unavailable
 * (notably in an iframe or a local offline copy). Exiting either mode always restores the article.
 */
async function setFullscreen(active) {
  if (active) {
    applyFullscreenLayout(true);
    if (document.fullscreenEnabled && !document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen();
        ownsNativeFullscreen = true;
      } catch {
        ownsNativeFullscreen = false;
      }
    }
    return;
  }

  ownsNativeFullscreen = false;
  if (document.fullscreenElement) {
    try { await document.exitFullscreen(); } catch { /* The focus layout can still exit. */ }
  }
  applyFullscreenLayout(false);
}

fullscreenToggle.addEventListener('click', () => {
  void setFullscreen(!document.body.classList.contains('atlas-fullscreen'));
});
fullscreenExit.addEventListener('click', () => { void setFullscreen(false); });
document.addEventListener('fullscreenchange', () => {
  if (document.fullscreenElement) {
    ownsNativeFullscreen = true;
    applyFullscreenLayout(true);
  } else if (ownsNativeFullscreen) {
    ownsNativeFullscreen = false;
    applyFullscreenLayout(false);
  }
});

// --- folds -------------------------------------------------------------------
const hintRules = document.getElementById('hintRules');
const hintIC = document.getElementById('hintIC');
const hintLook = document.getElementById('hintLook');

/** The class name without its trailing gloss — "Totalistic", not "Totalistic — all 256". */
const shortLabel = (cls) => cls.label.replace(/ —.*/, '');

/**
 * Say what each closed section is set to, in its own summary.
 *
 * This is what makes the folds usable rather than merely tidy: a control you have to open the
 * drawer to read is a control you stop putting away. Every one of these is a setting you would
 * otherwise have to remember you changed.
 */
function syncHints() {
  const cls = currentClass();
  // `chip`, not the full label: a summary line in a 22rem rail has room for about twenty characters
  // before it starts wrapping under its own heading.
  hintRules.textContent = `${cls.chip} · ${isExhaustive() ? 'complete' : sampleSeed.toString(36)}`;
  hintIC.textContent = icMode === 'single' ? 'Single seed'
    : icMode === 'clusters' ? `Clusters · ${clusterParams.count} clumps`
      : `Random · ${Number(densityInput.value).toFixed(2)}`;
  const palette = paletteSelect.selectedOptions[0];
  hintLook.textContent = (palette ? palette.textContent.replace(/ ·.*/, '').replace(' ✓', '') : '')
    + (Number(hueShiftInput.value) ? ` · ${hueShiftInput.value}°` : '')
    + (flicker.checked ? '' : ' · flash');
}

/** Push the tile grid into the element and into the box that holds it. */
function applyGridShape() {
  grid.setAttribute('layout', `${layoutCols}x${layoutRows}`);
  grid.style.setProperty('--tile-cols', String(layoutCols));
  grid.style.setProperty('--tile-rows', String(layoutRows));
  selectedIndex = -1;
  marker.hidden = true;
  fitStage();
}

function syncGridInputs() {
  gridColsInput.value = String(layoutCols);
  gridRowsInput.value = String(layoutRows);
  tileCountOut.textContent = String(tileCount());
}

/**
 * Re-tile the map.
 *
 * `layout` is a live attribute — the renderer just re-divides the canvas — but the tile *count* is
 * not: a ruleset list of a different length rebuilds the worlds, which is what `applySample` goes on
 * to do. Setting the attribute first means the rebuilt grid boots straight into the right shape
 * instead of laying itself out twice.
 */
function setLayout(cols, rows, shrink) {
  const next = clampLayout(cols, rows, shrink);
  if (next.cols === layoutCols && next.rows === layoutRows) {
    syncGridInputs();   // Snap a rejected keystroke back to what is actually on screen.
    return;
  }
  layoutCols = next.cols;
  layoutRows = next.rows;
  syncGridInputs();
  applyGridShape();
  applySample();
}

gridColsInput.max = String(AXIS_MAX);
gridRowsInput.max = String(AXIS_MAX);
// `change`, not `input`: mid-typing a cleared field reads as 1, and rebuilding 400 worlds for a
// value nobody meant is expensive as well as jarring.
gridColsInput.addEventListener('change', () => setLayout(gridColsInput.value, layoutRows, 'rows'));
gridRowsInput.addEventListener('change', () => setLayout(layoutCols, gridRowsInput.value, 'cols'));

for (const cls of CLASSES) {
  const opt = document.createElement('option');
  opt.value = cls.key;
  opt.textContent = cls.label;
  classSelect.append(opt);
}
classSelect.value = classKey;
classSelect.addEventListener('change', () => {
  classKey = classSelect.value;
  applySample();
});

biasInput.value = String(bias);
biasOut.textContent = bias.toFixed(2);
biasInput.addEventListener('input', () => {
  bias = Number(biasInput.value);
  biasOut.textContent = bias.toFixed(2);
  applySample();
});

rerollBtn.addEventListener('click', () => {
  sampleSeed = randomSeed();
  applySample();
});

for (const btn of document.querySelectorAll('.ic')) {
  btn.addEventListener('click', () => {
    icMode = btn.dataset.ic;
    syncICButtons();
    applyIC();
    writeUrl();
  });
}

function syncICButtons() {
  for (const b of document.querySelectorAll('.ic')) {
    b.setAttribute('aria-pressed', String(b.dataset.ic === icMode));
  }
  // Density only means anything for the element's own random fill.
  densityGroup.classList.toggle('off', icMode !== 'random');
  densityInput.disabled = icMode !== 'random';
  clusterPanel.hidden = icMode !== 'clusters';
  syncHints();
}

speedInput.addEventListener('input', () => {
  speedOut.textContent = `${speedInput.value}/s`;
  grid.setAttribute('speed', speedInput.value);
  detailWorld()?.setAttribute('speed', speedInput.value);
});

densityInput.addEventListener('input', () => {
  densityOut.textContent = Number(densityInput.value).toFixed(2);
  grid.setAttribute('density', densityInput.value);
  detailWorld()?.setAttribute('density', densityInput.value);
  syncHints();
  writeUrl();
});

const paletteGroups = {
  visual: Object.assign(document.createElement('optgroup'), {label: 'Color ramps'}),
  logic: Object.assign(document.createElement('optgroup'), {label: 'Rule-aware'}),
};
for (const p of listPresetPalettes()) {
  const opt = document.createElement('option');
  opt.value = p.key;
  opt.textContent = p.name + (p.logic ? ' · by rule group' : '') + (p.cvdSafe ? ' ✓' : '');
  paletteGroups[p.logic ? 'logic' : 'visual'].append(opt);
}
paletteSelect.append(paletteGroups.visual, paletteGroups.logic);
paletteSelect.value = 'monochrome';
paletteSelect.addEventListener('change', () => {
  // Live on both: a palette is a recolor, not a new world.
  grid.setAttribute('palette', paletteSelect.value);
  detailWorld()?.setAttribute('palette', paletteSelect.value);
  syncHints();
});

hueShiftInput.addEventListener('input', () => {
  hueShiftOut.textContent = `${hueShiftInput.value}°`;
  grid.setAttribute('hue-shift', hueShiftInput.value);
  detailWorld()?.setAttribute('hue-shift', hueShiftInput.value);
  syncHints();
});

const flicker = document.getElementById('flicker');
flicker.addEventListener('change', () => {
  // Presence is what counts — `flicker-proof` is a boolean attribute, and it only means anything
  // for preset palettes (a custom gradient is the host's own colors, not ours to rewrite).
  for (const el of [grid, detailWorld()]) {
    if (!el) continue;
    if (flicker.checked) el.setAttribute('flicker-proof', '');
    else el.removeAttribute('flicker-proof');
  }
  syncHints();
});

// --- cluster panel -----------------------------------------------------------
const clusterInputs = new Map();
{
  const mount = document.getElementById('clusterSliders');
  for (const def of CLUSTER_CONTROLS) {
    if (def.head) {
      const h = document.createElement('div');
      h.className = 'head';
      h.textContent = def.head;
      mount.append(h);
      continue;
    }
    const row = document.createElement('div');
    row.className = 'slider';

    const label = document.createElement('label');
    label.textContent = def.label;
    label.htmlFor = `cl-${def.key}`;
    label.title = def.help;

    const input = document.createElement('input');
    input.type = 'range';
    input.id = `cl-${def.key}`;
    input.min = String(def.min);
    input.max = String(def.max);
    input.step = String(def.step);
    input.value = String(clusterParams[def.key]);

    const out = document.createElement('span');
    out.className = 'readout';

    const decimals = def.step < 1 ? String(def.step).split('.')[1].length : 0;
    const show = () => { out.textContent = Number(input.value).toFixed(decimals); };
    show();

    input.addEventListener('input', () => {
      clusterParams[def.key] = Number(input.value);
      show();
      syncPresetChips();
      syncHints();
      applyICSoon();
      writeUrl();
    });

    clusterInputs.set(def.key, {input, show});
    row.append(label, input, out);
    mount.append(row);
  }
}

const presetButtons = [];
{
  const mount = document.getElementById('clusterPresets');
  for (const preset of CLUSTER_PRESETS) {
    const btn = document.createElement('button');
    btn.textContent = preset.name;
    btn.setAttribute('aria-pressed', 'false');
    btn.addEventListener('click', () => {
      // A preset is merged over the defaults exactly as the Explorer's modal does it, so the bundles
      // stay byte-identical to the frozen ones rather than acquiring whatever was on the sliders.
      Object.assign(clusterParams, CLUSTER_DEFAULTS, preset.params);
      for (const [key, {input, show}] of clusterInputs) {
        input.value = String(clusterParams[key]);
        show();
      }
      syncPresetChips();
      syncHints();
      applyIC();
      writeUrl();
    });
    presetButtons.push({btn, preset});
    mount.append(btn);
  }
}

/** Light the chip whose bundle exactly matches the sliders; none = custom. */
function syncPresetChips() {
  for (const {btn, preset} of presetButtons) {
    const full = {...CLUSTER_DEFAULTS, ...preset.params};
    const match = CLUSTER_KEYS.every((k) => Math.abs(full[k] - clusterParams[k]) < 1e-9);
    btn.setAttribute('aria-pressed', String(match));
  }
}

document.getElementById('reshuffleBtn').addEventListener('click', () => {
  clusterSeed = randomSeed();
  applyIC();
  writeUrl();
});

// --- sample ------------------------------------------------------------------
/** Redraw the class sample, push it to the grid, and restate what is on screen. */
function applySample() {
  const cls = currentClass();
  // Not a property of the class any more: 256 totalistic rules are the whole map at 16×16 and a
  // space to sample at 8×8, so bias and reroll switch on the moment the grid gets smaller than the
  // class — the same controls, doing the same thing they do for every wider class.
  const exhaustive = isExhaustive();

  biasGroup.classList.toggle('off', exhaustive);
  biasInput.disabled = exhaustive;
  rerollGroup.classList.toggle('off', exhaustive);
  rerollBtn.disabled = exhaustive;
  seedOut.textContent = exhaustive ? '' : sampleSeed.toString(36);

  const {codes, distinct} = drawSample(cls, sampleSeed, bias, tileCount());
  currentCodes = codes;
  distinctCount = distinct;

  // A same-length list swaps the rules on the live worlds and re-seeds them from the shared initial
  // cells — no rebuild, no lost IC, back to generation 0. Which is why the grid is always 256 tiles.
  // Unconditional: on the first call the element is still booting, and the setter's job then is to
  // park the list for `_boot` to read — a `grid.count` guard would leave the grid with no rules.
  grid.rulesets = codes;

  // The rule under the marker is not the rule that was clicked any more.
  selectedIndex = -1;
  marker.hidden = true;
  selectedOut.textContent = '';

  renderSample();
  syncHints();
  writeUrl();
  syncStatus();
}

function renderSample() {
  const cls = currentClass();
  const size = tileCount();
  const total = 1n << BigInt(cls.bits);
  const short = shortLabel(cls);
  const shape = `${layoutCols}×${layoutRows}`;

  if (classSize(cls) <= size) {
    // The spare tiles are repeats, and saying so is the whole reason this line exists: a map that
    // quietly showed the same rule twice would be indistinguishable from one that did not.
    const spare = size - classSize(cls);
    sampleOut.innerHTML = `<b>${short}</b> — showing <b>all ${fmtBig(total)}</b> rulesets in the class `
      + `(2<sup>${cls.bits}</sup>) on a ${shape} grid. This is the complete class, not a sample: every `
      + `totalistic rule is on screen`
      + (spare ? `, and the ${spare} tiles left over repeat from the start.` : ' exactly once.')
      + `<span class="note">${cls.note}</span>`;
    return;
  }

  // BigInt division truncates, which turns "1 in 1.8" into the nonsense "1 in 1" as soon as the
  // grid is a large fraction of the class — reachable now that the grid is a knob. Float is exact
  // enough for the small ratios and only the big ones need BigInt anyway.
  const ratioNum = Number(total) / size;
  const ratioText = ratioNum < 100
    ? ratioNum.toFixed(ratioNum < 10 ? 1 : 0)
    : fmtBig(total / BigInt(size));
  const biasNote = bias === 0.5
    ? 'drawn uniformly'
    : `drawn with each ${cls.unit} live at <em>p</em> = ${bias.toFixed(2)}, so the sample is biased toward `
      + `${bias < 0.5 ? 'sparse, quiet' : 'dense, saturating'} rules rather than uniform`;
  const shortfall = distinctCount < size
    ? ` <b>Only ${distinctCount} distinct rules were reachable at this bias</b>, so ${size - distinctCount} tiles repeat.`
    : '';
  // A class that would fit a bigger grid gets told so, because that is a knob away rather than a
  // fact of the universe — unlike the 2^128 the free class is a slice of.
  const roomier = classSize(cls) <= MAX_TILES
    ? ` The whole class fits a ${Math.ceil(Math.sqrt(classSize(cls)))}×${Math.ceil(Math.sqrt(classSize(cls)))} grid.`
    : '';
  sampleOut.innerHTML = `<b>${short}</b> — showing a random subset of <b>${size}</b> of the `
    + `<b>${fmtBig(total)}</b> rulesets (2<sup>${cls.bits}</sup>) that satisfy this constraint: `
    + `1 in ${ratioText}, on a ${shape} grid.${shortfall}${roomier}`
    + `<span class="note">${cls.note} Sample <code>${sampleSeed.toString(36)}</code>, ${biasNote}.</span>`;
}

// --- url ---------------------------------------------------------------------
/**
 * Everything that decides what is on screen goes in the query string, so a reroll that turns up
 * something worth keeping is a link rather than a screenshot. Palette and speed stay out: they are
 * how it looks, not what it is.
 */
let urlPending = 0;
function writeUrl() {
  if (urlPending) return;
  urlPending = requestAnimationFrame(() => {
    urlPending = 0;
    const p = new URLSearchParams();
    p.set('c', classKey);
    // Always written, even when it matches the default: the default is now whatever the screen that
    // opened the link could draw, so leaving it out would reshape somebody else's view of it.
    p.set('g', `${layoutCols}x${layoutRows}`);
    if (!isExhaustive()) {
      p.set('s', sampleSeed.toString(36));
      if (bias !== 0.5) p.set('b', bias.toFixed(2));
    }
    p.set('ic', icMode);
    if (icMode === 'random') p.set('d', densityInput.value);
    if (icMode === 'clusters') {
      p.set('cl', CLUSTER_KEYS.map((k) => clusterParams[k]).join(','));
      p.set('cs', clusterSeed.toString(36));
    }
    if (document.body.classList.contains('atlas-fullscreen')) p.set('focus', '1');
    history.replaceState(null, '', `${window.location.pathname}?${p}`);
  });
}

// --- status ------------------------------------------------------------------
const genOut = document.getElementById('gen');
const selectedOut = document.getElementById('selected');

function syncStatus() {
  genOut.textContent = grid.generation.toLocaleString();
}
grid.addEventListener('hexlife-playstate', (e) => {
  playBtn.textContent = e.detail.userPaused ? 'Play' : 'Pause';
});
grid.addEventListener('hexlife-ready', (e) => {
  document.getElementById('worlds').textContent = e.detail.worlds;
  document.getElementById('cells').textContent = `${e.detail.rows}×${e.detail.cols}`;
  // A rebuild is the one thing that moves every tile without the page resizing.
  fitStage();
  applyIC();
});
setInterval(syncStatus, 250);

// --- selection ---------------------------------------------------------------
const detail = document.getElementById('detail');
const detailMount = document.getElementById('detailMount');

/** The live detail world, or null while the dialog is closed. */
const detailWorld = () => detailMount.firstElementChild;

/** Guards against a close (or a second click) landing while an encode is still in flight. */
let detailToken = 0;

/**
 * Mount a full-size world for one rule, **on the initial condition currently selected**.
 *
 * The two IC kinds get there by different routes, and the reason is worth stating: `seed` +
 * `density` is a *recipe*, so a world given the same ruleset, rows, seed and density regenerates the
 * grid tile's exact tick 0 for free — determinism is the package's whole contract. A painted IC
 * (single seed, clusters) is not a recipe and no attribute can express it, so it travels as an
 * `HXW1.` world code carrying the exact cells. That also makes the element's own reset button replay
 * the seed rather than rolling a random fill, which is what "open this tile" should mean.
 *
 * Removing the element on close frees its WebGL context — the scarce resource this whole page is
 * about.
 */
async function openDetail(rulesetHex) {
  const token = ++detailToken;
  detailMount.replaceChildren();
  const w = document.createElement('hexlife-world');

  if (icTemplate) {
    const {rows, cols} = grid.worldAt(0);
    const code = await encodeWorldCode({
      rows, cols, rulesetHex,
      cells: icTemplate,
      speed: Number(speedInput.value),
      // A palette is mandatory (encodeWorldCode returns null without one). The `palette` attribute
      // below overrides it anyway, but the code has to be a complete world on its own.
      colorSettings: {
        mode: 'preset',
        activePreset: paletteSelect.value,
        flickerProofPresets: flicker.checked,
        hueShift: Number(hueShiftInput.value),
      },
    });
    if (token !== detailToken) return;    // Closed, or superseded by another tile, mid-encode.
    // A null code means the codec refused the inputs; the recipe below is still a real world, so
    // fall through to it rather than showing nothing.
    if (code) w.setAttribute('code', code);
  }

  if (!w.hasAttribute('code')) {
    w.setAttribute('ruleset', rulesetHex);
    w.setAttribute('rows', '96');
    w.setAttribute('seed', '12345');
    w.setAttribute('density', densityInput.value);
  }
  // Playback and appearance are not part of the world, so these apply either way.
  w.setAttribute('speed', speedInput.value);
  w.setAttribute('palette', paletteSelect.value);
  w.setAttribute('hue-shift', hueShiftInput.value);
  if (flicker.checked) w.setAttribute('flicker-proof', '');

  detailMount.append(w);
  if (!detail.open) detail.showModal();
}

detail.addEventListener('close', () => { detailToken++; detailMount.replaceChildren(); });

grid.addEventListener('hexlife-worldselect', (e) => {
  const {index, rulesetHex} = e.detail;
  // Not `currentCodes[index]`: `rulesetToCode` tags with the *strictest* class the rule actually
  // satisfies, so a draw from the C6 class that happens to be totalistic says so.
  const code = rulesetToCode(rulesetHex) || rulesetHex;

  selectedIndex = index;
  positionMarker();

  const desc = describeRuleset(rulesetHex);
  selectedOut.innerHTML = `selected <b>${code}</b> · ${desc?.notation || 'no B/S form'} · ${rulesetName(rulesetHex)}`;

  document.getElementById('detailTitle').textContent = `${code} — ${rulesetName(rulesetHex)}`;
  document.getElementById('detailCode').textContent = desc?.notation || rulesetHex;
  document.getElementById('detailLink').href = explorerUrlForRuleset(rulesetHex, {rows: 96});
  document.getElementById('detailMeta').textContent =
    `${rulesetHex} · generation ${e.detail.tick} · ${e.detail.activeCount.toLocaleString()} live cells when clicked`;

  openDetail(rulesetHex);
});

document.getElementById('detailClose').addEventListener('click', () => detail.close());

grid.addEventListener('hexlife-error', (e) => {
  selectedOut.innerHTML = `<b style="color:#e06c5a">${e.detail.message}</b> ${e.detail.detail}`;
});

// --- keyboard ----------------------------------------------------------------
/**
 * The four things you do over and over while hunting through a class, on the keys the Explorer
 * already uses for them: Space/`p` play-pause, `r` reset, `g` reroll (its "generate new ruleset"),
 * `→` step. Routed through the buttons rather than the handlers so a disabled control — Reroll,
 * while the totalistic class is exhaustive — stays disabled from the keyboard too.
 *
 * Typing in a field always wins, so the arrow keys still nudge a focused slider. Space is
 * `preventDefault`ed: left alone it would scroll the page, and re-fire whichever button was clicked
 * last instead of toggling playback.
 */
const HOTKEYS = {
  ' ': playBtn,
  p: playBtn,
  r: resetBtn,
  g: rerollBtn,
  c: panelToggle,
  f: fullscreenToggle,
  arrowright: stepBtn,
  '.': stepBtn,
};
window.addEventListener('keydown', (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  const el = document.activeElement;
  if (el && (el.matches('input, select, textarea') || el.isContentEditable)) return;
  if (e.key === 'Escape' && document.body.classList.contains('atlas-fullscreen')
      && !document.fullscreenElement) {
    e.preventDefault();
    void setFullscreen(false);
    return;
  }
  const btn = HOTKEYS[e.key === ' ' ? ' ' : e.key.toLowerCase()];
  if (!btn) return;
  // Held keys repeat only for Step — that is scrubbing. A repeating play-pause is a strobe, and a
  // repeating reroll would burn through the class faster than the eye can follow.
  if (e.repeat && btn !== stepBtn) return;
  e.preventDefault();
  btn.click();
});

// --- boot --------------------------------------------------------------------
densityInput.value = String(num(q.get('d'), 0.02, 0.98, 0.5));
densityOut.textContent = Number(densityInput.value).toFixed(2);
grid.setAttribute('density', densityInput.value);
// Where there is room for it the argument is worth reading; where there is not, those eight lines
// are eight lines the map does not get — and the class banner makes the same point in two.
document.querySelector('.about').open =
  window.matchMedia('(min-width: 900px) and (min-height: 940px)').matches;
// A link that arrives with cluster params is a link about the clusters; the other two IC modes are
// one button each and do not need the drawer opened for them.
if (icMode === 'clusters') document.getElementById('foldIC').open = true;
syncICButtons();
syncPresetChips();
syncGridInputs();
applyGridShape();
applySample();
// A direct workspace-only URL is useful for kiosks, captures, and hosts where native fullscreen
// needs a user gesture. The visible button upgrades the same layout to browser fullscreen.
if (q.get('focus') === '1') applyFullscreenLayout(true);

// Debug handle, in the house style.
window.__totalistic = {
  grid, CLASSES, applyIC, applySample, clusterParams, setLayout, fitStage, setFullscreen,
  get codes() { return currentCodes; },
  get icMode() { return icMode; },
  get classKey() { return classKey; },
  get layout() { return {cols: layoutCols, rows: layoutRows, tiles: tileCount()}; },
  get distinct() { return distinctCount; },
};
